import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import { toJalaliDate, toJalaliFullDate } from "../../../../middleware/middleware";
import ContractorCostQuotesApi from "../../../../../infrastructure/api/contractorCostQuotes";
import {
  CONTRACTOR_COST_STATUS_CODES,
  normalizeContractorCostQuoteStatus,
} from "../../../../../infrastructure/utils/pricingQuoteStatus";

const DRAFT_STATUS = CONTRACTOR_COST_STATUS_CODES.Draft;
const SUBMITTED_STATUS = CONTRACTOR_COST_STATUS_CODES.Submitted;
const APPROVED_STATUS = CONTRACTOR_COST_STATUS_CODES.Approved;
const REJECTED_STATUS = CONTRACTOR_COST_STATUS_CODES.Rejected;

const proposalModeOptions = [
  {
    mode: "package",
    quoteType: 2,
    label: "پکیج تجهیزات + دستمزد",
    hint: "با انتخاب پکیج، ریز تجهیزات و قیمت تمام‌شده از پکیج بارگذاری می‌شود.",
  },
  {
    mode: "execution",
    quoteType: 3,
    label: "فقط دستمزد / اجرا",
    hint: "اگر فقط اجرا می‌کنید و تجهیزات از قبل تعیین شده یا بر عهده شما نیست.",
  },
  {
    mode: "custom",
    quoteType: 2,
    label: "تجهیزات سفارشی (بدون پکیج)",
    hint: "وقتی پکیج آماده ندارید، ریز تجهیزات و قیمت هر قلم را همین‌جا وارد کنید.",
  },
];

const statusColor = (status) => {
  const s = Number(status);
  if (s === APPROVED_STATUS) return "success";
  if (s === SUBMITTED_STATUS) return "warning";
  if (s === REJECTED_STATUS) return "error";
  return "default";
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("fa-IR");

const normalizeQuote = (raw) => ({
  id: raw?.id ?? raw?.Id,
  requestId: raw?.requestId ?? raw?.RequestId,
  quoteType: raw?.quoteType ?? raw?.QuoteType,
  quoteTypeTitle: raw?.quoteTypeTitle ?? raw?.QuoteTypeTitle ?? "",
  status: normalizeContractorCostQuoteStatus(raw?.status ?? raw?.Status),
  statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
  revisionNo: raw?.revisionNo ?? raw?.RevisionNo ?? 1,
  executionCost: raw?.executionCost ?? raw?.ExecutionCost,
  equipmentCost: raw?.equipmentCost ?? raw?.EquipmentCost,
  logisticsCost: raw?.logisticsCost ?? raw?.LogisticsCost,
  otherCost: raw?.otherCost ?? raw?.OtherCost,
  totalContractorCost: raw?.totalContractorCost ?? raw?.TotalContractorCost ?? 0,
  packageId: raw?.packageId ?? raw?.PackageId,
  packageTitle: raw?.packageTitle ?? raw?.PackageTitle ?? "",
  equipmentSnapshotId: raw?.equipmentSnapshotId ?? raw?.EquipmentSnapshotId,
  estimatedDurationDays: raw?.estimatedDurationDays ?? raw?.EstimatedDurationDays,
  validUntil: raw?.validUntil ?? raw?.ValidUntil ?? null,
  submittedAt: raw?.submittedAt ?? raw?.SubmittedAt,
  rejectReason: raw?.rejectReason ?? raw?.RejectReason,
  parentCostQuoteId: raw?.parentCostQuoteId ?? raw?.ParentCostQuoteId,
});

const normalizeRequest = (raw) => ({
  requestId: raw?.requestId ?? raw?.RequestId ?? raw?.id ?? raw?.Id ?? 0,
  requestTitle: raw?.requestTitle ?? raw?.RequestTitle ?? "",
  requestReferenceId:
    raw?.requestReferenceId ??
    raw?.RequestReferenceId ??
    raw?.referenceID ??
    "",
  applicantFullName:
    raw?.applicantFullName ?? raw?.ApplicantFullName ?? "",
  plantCapacityTitle:
    raw?.plantCapacityTitle ?? raw?.PlantCapacityTitle ?? "",
  powerPlantTypeTitle:
    raw?.powerPlantTypeTitle ?? raw?.PowerPlantTypeTitle ?? "",
  provinceTitle: raw?.provinceTitle ?? raw?.ProvinceTitle ?? "",
  cityTitle: raw?.cityTitle ?? raw?.CityTitle ?? "",
  address: raw?.address ?? raw?.Address ?? "",
  installationTypeTitle:
    raw?.installationTypeTitle ?? raw?.InstallationTypeTitle ?? "",
  locationUsageTypeTitle:
    raw?.locationUsageTypeTitle ?? raw?.LocationUsageTypeTitle ?? "",
  installableAreaSquareMeter:
    raw?.installableAreaSquareMeter ?? raw?.InstallableAreaSquareMeter ?? null,
  needsLoan: Boolean(raw?.needsLoan ?? raw?.NeedsLoan),
  siteVisit: (() => {
    const sv = raw?.siteVisit ?? raw?.SiteVisit;
    if (!sv) return null;
    return {
      siteVisitId: sv.siteVisitId ?? sv.SiteVisitId,
      siteVisitStatusTitle:
        sv.siteVisitStatusTitle ?? sv.SiteVisitStatusTitle ?? "",
      requestedCapacityKw:
        sv.requestedCapacityKw ?? sv.RequestedCapacityKw ?? null,
      proposedCapacityKw:
        sv.proposedCapacityKw ?? sv.ProposedCapacityKw ?? null,
      phaseCountTitle: sv.phaseCountTitle ?? sv.PhaseCountTitle ?? "",
      installationLocationTitle:
        sv.installationLocationTitle ?? sv.InstallationLocationTitle ?? "",
      installableAreaSquareMeter:
        sv.installableAreaSquareMeter ?? sv.InstallableAreaSquareMeter ?? null,
      hasMeterLocation: sv.hasMeterLocation ?? sv.HasMeterLocation,
      isFullSunlight: sv.isFullSunlight ?? sv.IsFullSunlight,
      isSharedRooftop: sv.isSharedRooftop ?? sv.IsSharedRooftop,
      description: sv.description ?? sv.Description ?? "",
      actualVisitTime: sv.actualVisitTime ?? sv.ActualVisitTime ?? null,
    };
  })(),
  suggestedPackageId: raw?.suggestedPackageId ?? raw?.SuggestedPackageId,
  suggestedPackageTitle:
    raw?.suggestedPackageTitle ?? raw?.SuggestedPackageTitle ?? "",
});

const buildProjectSummaryLabel = (request) => {
  if (!request) return "درخواست";
  if (request.requestTitle) return request.requestTitle;
  const parts = [
    request.requestReferenceId || request.requestId,
    request.applicantFullName,
    request.plantCapacityTitle,
    request.powerPlantTypeTitle,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" — ") : `درخواست ${request.requestId}`;
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", py: 0.25 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "—"}
    </Typography>
  </Box>
);

const boolFa = (value) => {
  if (value === true) return "بله";
  if (value === false) return "خیر";
  return "—";
};

const emptyLineItem = () => ({
  lineType: 2,
  title: "",
  quantity: 1,
  unit: "pcs",
  unitCost: "",
});

const ContractorCostQuotesPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submittingId, setSubmittingId] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [quoteableRequests, setQuoteableRequests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [packagePricing, setPackagePricing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(0);
  /** پس از دیدن اطلاعات پروژه، پیمانکار مجاز به انتخاب پکیج و ثبت پیشنهاد می‌شود. */
  const [projectReviewed, setProjectReviewed] = useState(false);

  const [form, setForm] = useState({
    requestId: "",
    parentCostQuoteId: "",
    proposalMode: "package",
    executionCost: "",
    equipmentCost: "",
    logisticsCost: "",
    otherCost: "",
    estimatedDurationDays: "",
    estimatedStartDate: "",
    validUntil: "",
    description: "",
    technicalNotes: "",
    packageId: "",
  });
  const [lineItems, setLineItems] = useState([emptyLineItem()]);

  const selectedMode = useMemo(
    () => proposalModeOptions.find((x) => x.mode === form.proposalMode),
    [form.proposalMode]
  );

  const selectedRequest = useMemo(
    () =>
      quoteableRequests.find((x) => String(x.requestId) === String(form.requestId)) ||
      null,
    [quoteableRequests, form.requestId]
  );

  const packageEquipmentTotal = useMemo(
    () =>
      previewItems.reduce(
        (sum, row) => sum + Number(row.totalCost ?? row.TotalCost ?? 0),
        0
      ),
    [previewItems]
  );

  const customEquipmentTotal = useMemo(
    () =>
      lineItems.reduce((sum, row) => {
        if (!row.title?.trim()) return sum;
        const qty = Number(row.quantity) > 0 ? Number(row.quantity) : 1;
        const unit = Number(row.unitCost) || 0;
        return sum + qty * unit;
      }, 0),
    [lineItems]
  );

  const computedTotal = useMemo(() => {
    const projectCosts =
      (Number(form.executionCost) || 0) +
      (Number(form.logisticsCost) || 0) +
      (Number(form.otherCost) || 0);

    if (form.proposalMode === "custom") {
      return projectCosts + customEquipmentTotal;
    }
    if (form.proposalMode === "package") {
      return projectCosts + (Number(form.equipmentCost) || packageEquipmentTotal || 0);
    }
    return projectCosts;
  }, [form, customEquipmentTotal, packageEquipmentTotal]);

  const requestLabels = useMemo(() => {
    const map = {};
    quoteableRequests.forEach((r) => {
      if (Number(r.requestId) > 0) {
        map[String(r.requestId)] = buildProjectSummaryLabel(r);
      }
    });
    return map;
  }, [quoteableRequests]);

  const lockedRequestIds = useMemo(() => {
    const ids = new Set();
    quotes.forEach((q) => {
      if (Number(q.status) === APPROVED_STATUS && q.requestId) {
        ids.add(String(q.requestId));
      }
    });
    return ids;
  }, [quotes]);

  const selectedRequestLocked = lockedRequestIds.has(String(form.requestId));

  const loadData = async () => {
    setLoading(true);
    setError("");
    const [quotesRes, requestsRes, packagesRes] = await Promise.all([
      ContractorCostQuotesApi.getMyCostQuotes(),
      ContractorCostQuotesApi.getMyCostQuoteableRequests(),
      ContractorCostQuotesApi.getContractorPackages(),
    ]);

    if (!quotesRes.ok) {
      setError(quotesRes.message || "خطا در دریافت پیشنهادهای هزینه");
      setQuotes([]);
    } else {
      const list = Array.isArray(quotesRes.data) ? quotesRes.data : [];
      setQuotes(list.map(normalizeQuote));
    }

    if (!requestsRes.ok) {
      setQuoteableRequests([]);
    } else {
      const list = Array.isArray(requestsRes.data) ? requestsRes.data : [];
      setQuoteableRequests(
        list.map(normalizeRequest).filter((x) => Number(x.requestId) > 0)
      );
    }

    if (packagesRes.ok) {
      setPackages(Array.isArray(packagesRes.data) ? packagesRes.data : []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadPackagePreview = useCallback(async (packageId) => {
    if (!packageId) {
      setPreviewItems([]);
      setPackagePricing(null);
      setForm((prev) => ({ ...prev, equipmentCost: "" }));
      return;
    }
    setPreviewLoading(true);
    const res = await ContractorCostQuotesApi.previewPackageEquipment(Number(packageId));
    if (!res.ok) {
      setPreviewItems([]);
      setPackagePricing(null);
      setError(res.message || "خطا در دریافت ریز تجهیزات پکیج");
    } else {
      const data = res.data || {};
      const items = data.items ?? data.Items ?? [];
      setPreviewItems(Array.isArray(items) ? items : []);

      const totalEquipment = Number(
        data.totalEquipmentCost ?? data.TotalEquipmentCost ?? 0
      );
      const labor = Number(
        data.priceWithoutEquipment ?? data.PriceWithoutEquipment ?? 0
      );
      const deliveryFee = Number(data.deliveryFee ?? data.DeliveryFee ?? 0);
      const contractorPaysDelivery = Boolean(
        data.contractorPaysDelivery ?? data.ContractorPaysDelivery
      );
      const logistics = contractorPaysDelivery ? 0 : deliveryFee;
      const duration =
        data.installationDurationDays ?? data.InstallationDurationDays;
      const finished = Number(
        data.priceWithEquipment ?? data.PriceWithEquipment ?? 0
      );
      const suggested = Number(
        data.suggestedTotalCost ?? data.SuggestedTotalCost ?? 0
      );

      setPackagePricing({
        title: data.packageTitle ?? data.PackageTitle ?? "",
        totalEquipment,
        labor,
        deliveryFee,
        contractorPaysDelivery,
        finished,
        suggested,
        duration: duration != null ? Number(duration) : null,
        priceValidUntil:
          data.priceValidUntil ?? data.PriceValidUntil ?? null,
      });

      setForm((prev) => ({
        ...prev,
        equipmentCost: totalEquipment > 0 ? String(totalEquipment) : "",
        executionCost:
          labor > 0
            ? String(labor)
            : prev.executionCost || "",
        logisticsCost:
          logistics > 0
            ? String(logistics)
            : contractorPaysDelivery
            ? ""
            : prev.logisticsCost || "",
        estimatedDurationDays:
          duration != null && Number(duration) > 0
            ? String(duration)
            : prev.estimatedDurationDays || "",
        validUntil:
          data.priceValidUntil ??
          data.PriceValidUntil ??
          prev.validUntil ??
          "",
      }));
    }
    setPreviewLoading(false);
  }, []);

  useEffect(() => {
    if (form.proposalMode !== "package") {
      setPreviewItems([]);
      setPackagePricing(null);
      return;
    }
    loadPackagePreview(form.packageId);
  }, [form.proposalMode, form.packageId, loadPackagePreview]);

  const handleField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "requestId") {
        // انتخاب پروژه فقط اطلاعات را نشان می‌دهد؛ پکیج را خودکار پر نکن
        next.packageId = "";
        next.proposalMode = "package";
        next.parentCostQuoteId = "";
        next.executionCost = "";
        next.equipmentCost = "";
        next.logisticsCost = "";
        next.otherCost = "";
        next.estimatedDurationDays = "";
        next.estimatedStartDate = "";
        next.validUntil = "";
        setProjectReviewed(false);
        setPreviewItems([]);
        setPackagePricing(null);
        setLineItems([emptyLineItem()]);
      }
      if (field === "proposalMode") {
        if (value === "execution") {
          next.equipmentCost = "";
          next.packageId = "";
          next.validUntil = "";
        }
        if (value === "package") {
          next.equipmentCost = "";
        }
        if (value === "custom") {
          next.packageId = "";
          next.equipmentCost = "";
          next.validUntil = "";
        }
      }
      return next;
    });
  };

  const buildPayload = () => {
    const mode = selectedMode || proposalModeOptions[0];
    const payload = {
      requestId: Number(form.requestId) || 0,
      parentCostQuoteId:
        Number(form.parentCostQuoteId) > 0 ? Number(form.parentCostQuoteId) : null,
      quoteType: mode.quoteType,
      executionCost: Number(form.executionCost) || null,
      equipmentCost:
        form.proposalMode === "execution"
          ? null
          : form.proposalMode === "custom"
          ? customEquipmentTotal || null
          : Number(form.equipmentCost) || packageEquipmentTotal || null,
      logisticsCost: Number(form.logisticsCost) || null,
      otherCost: Number(form.otherCost) || null,
      estimatedDurationDays: Number(form.estimatedDurationDays) || null,
      estimatedStartDate: form.estimatedStartDate || null,
      validUntil:
        form.proposalMode === "package"
          ? packagePricing?.priceValidUntil || form.validUntil || null
          : form.validUntil || null,
      description: form.description || "",
      technicalNotes: form.technicalNotes || "",
      currencyCode: "IRR",
      packageId:
        form.proposalMode === "package" && Number(form.packageId) > 0
          ? Number(form.packageId)
          : null,
      lineItems: [],
    };

    if (form.proposalMode === "custom") {
      payload.lineItems = lineItems
        .filter((x) => x.title?.trim())
        .map((x, idx) => ({
          lineType: Number(x.lineType) || 2,
          title: x.title.trim(),
          quantity: Number(x.quantity) > 0 ? Number(x.quantity) : 1,
          unit: x.unit || "pcs",
          unitCost: Number(x.unitCost) || 0,
          sortOrder: idx,
        }));
    }

    return payload;
  };

  const handleCreateDraft = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    if (!form.requestId) {
      setError("پروژه مجاز را انتخاب کنید.");
      setSaving(false);
      return;
    }
    if (!projectReviewed) {
      setError("ابتدا اطلاعات پروژه را بررسی کنید و روی «ادامه به پیشنهاد قیمت» بزنید.");
      setSaving(false);
      return;
    }
    if (form.proposalMode === "package" && !form.packageId) {
      setError("برای استفاده از پکیج، ابتدا پکیج را انتخاب کنید.");
      setSaving(false);
      return;
    }
    if (form.proposalMode === "package" && previewItems.length === 0 && !previewLoading) {
      setError("پکیج انتخاب‌شده فاقد اقلام تجهیزات است. پکیج را در بخش مدیریت پکیج تکمیل کنید.");
      setSaving(false);
      return;
    }
    if (form.proposalMode === "custom") {
      const equipmentRows = lineItems.filter(
        (x) => x.title?.trim() && (Number(x.lineType) || 2) === 2
      );
      if (equipmentRows.length === 0) {
        setError("حداقل یک قلم تجهیزات با عنوان معتبر ثبت کنید.");
        setSaving(false);
        return;
      }
      const incomplete = equipmentRows.find(
        (x) => !(Number(x.quantity) > 0) || !(Number(x.unitCost) > 0)
      );
      if (incomplete) {
        setError("برای هر قلم تجهیزات، تعداد و قیمت واحد باید بزرگ‌تر از صفر باشد.");
        setSaving(false);
        return;
      }
    }
    if (form.proposalMode === "execution" && !(Number(form.executionCost) > 0)) {
      setError("هزینه اجرا / دستمزد الزامی است.");
      setSaving(false);
      return;
    }
    if (form.proposalMode === "package") {
      if (!packagePricing?.priceValidUntil && !form.validUntil) {
        setError(
          "این پکیج تاریخ اعتبار قیمت ندارد. ابتدا در مدیریت پکیج تاریخ اعتبار را ثبت کنید."
        );
        setSaving(false);
        return;
      }
    } else if (!form.validUntil) {
      setError("تاریخ اعتبار قیمت الزامی است.");
      setSaving(false);
      return;
    }
    if (computedTotal <= 0) {
      setError("جمع کل هزینه باید بزرگ‌تر از صفر باشد.");
      setSaving(false);
      return;
    }
    if (selectedRequestLocked) {
      setError("هزینه این پروژه توسط سولارنت تایید شده و ثبت پیشنهاد جدید مجاز نیست.");
      setSaving(false);
      return;
    }

    const api = form.parentCostQuoteId
      ? ContractorCostQuotesApi.createRevision
      : ContractorCostQuotesApi.createDraft;
    const res = await api(buildPayload());
    if (!res.ok) {
      setError(res.message || "ثبت پیش‌نویس ناموفق بود.");
    } else {
      setSuccess(res.message || "پیش‌نویس پیشنهاد هزینه ثبت شد.");
      setForm((prev) => ({ ...prev, parentCostQuoteId: "" }));
      await loadData();
    }
    setSaving(false);
  };

  const handleSubmit = async (costQuoteId) => {
    setSubmittingId(costQuoteId);
    setError("");
    const res = await ContractorCostQuotesApi.submitCostQuote(costQuoteId);
    if (!res.ok) {
      setError(res.message || "ارسال ناموفق بود.");
    } else {
      setSuccess(res.message || "پیشنهاد هزینه ارسال شد.");
      await loadData();
    }
    setSubmittingId(0);
  };

  const startRevision = (quote) => {
    setProjectReviewed(true);
    setForm((prev) => ({
      ...prev,
      requestId: String(quote.requestId || ""),
      parentCostQuoteId: String(quote.id || ""),
      proposalMode: quote.packageId
        ? "package"
        : quote.quoteType === 3
        ? "execution"
        : "custom",
      packageId: quote.packageId ? String(quote.packageId) : "",
      executionCost: quote.executionCost != null ? String(quote.executionCost) : "",
      logisticsCost: quote.logisticsCost != null ? String(quote.logisticsCost) : "",
      otherCost: quote.otherCost != null ? String(quote.otherCost) : "",
      equipmentCost: "",
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applySuggestedPackage = () => {
    if (!selectedRequest?.suggestedPackageId) return;
    setForm((prev) => ({
      ...prev,
      proposalMode: "package",
      packageId: String(selectedRequest.suggestedPackageId),
    }));
  };

  const beginQuoteAfterReview = () => {
    if (!form.requestId) {
      setError("ابتدا پروژه را انتخاب کنید.");
      return;
    }
    setProjectReviewed(true);
    setError("");
    setSuccess("");
  };

  const handleConfirmDeleteDraft = async () => {
    if (!deleteTarget?.id) {
      return { isSuccess: false, message: "پیشنهاد انتخاب نشده است." };
    }

    setDeletingId(deleteTarget.id);
    const res = await ContractorCostQuotesApi.deleteDraftCostQuote(deleteTarget.id);
    setDeletingId(0);

    if (res.ok) {
      setSuccess(res.message || "پیش‌نویس حذف شد.");
      setError("");
      await loadData();
    }

    return {
      isSuccess: res.ok,
      message: res.message || (res.ok ? "پیش‌نویس حذف شد." : "حذف ناموفق بود."),
    };
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="پیشنهاد هزینه پیمانکار"
            subheader="با انتخاب پکیج، قیمت تمام‌شده و جزئیات از مدیریت پکیج بارگذاری می‌شود"
            action={
              <IconButton onClick={loadData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              {error ? (
                <Alert severity="error" onClose={() => setError("")}>
                  {error}
                </Alert>
              ) : null}
              {success ? (
                <Alert severity="success" onClose={() => setSuccess("")}>
                  {success}
                </Alert>
              ) : null}

              <Alert severity="info" variant="outlined">
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  ترتیب کار
                </Typography>
                <Typography variant="body2" component="div">
                  <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                    <li>پروژه را انتخاب کنید و اطلاعات آن (و خلاصه بازدید) را ببینید.</li>
                    <li>پس از بررسی، نوع پیشنهاد و در صورت نیاز پکیج را انتخاب کنید.</li>
                    <li>پیش‌نویس را ثبت و سپس ارسال کنید.</li>
                  </Box>
                </Typography>
              </Alert>

              <Typography variant="subtitle1" fontWeight={700}>
                ثبت پیشنهاد جدید
                {form.parentCostQuoteId ? " (نسخه اصلاحی)" : ""}
              </Typography>

              {selectedRequestLocked ? (
                <Alert severity="warning">
                  هزینه این پروژه توسط کارشناس سولارنت تایید شده است. امکان ثبت یا ارسال
                  پیشنهاد هزینه جدید وجود ندارد.
                </Alert>
              ) : null}

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel>۱. انتخاب پروژه</InputLabel>
                    <Select
                      label="۱. انتخاب پروژه"
                      value={form.requestId}
                      onChange={(e) => handleField("requestId", e.target.value)}
                      renderValue={(selected) =>
                        requestLabels[String(selected)] || selected || ""
                      }
                    >
                      {quoteableRequests.map((r) => (
                        <MenuItem key={r.requestId} value={String(r.requestId)}>
                          <Box sx={{ py: 0.25 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {r.requestReferenceId || r.requestId}
                              {r.applicantFullName
                                ? ` — ${r.applicantFullName}`
                                : ""}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[
                                r.plantCapacityTitle,
                                r.powerPlantTypeTitle,
                                [r.provinceTitle, r.cityTitle].filter(Boolean).join(" / "),
                              ]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedRequest ? (
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderColor: projectReviewed ? "success.light" : "primary.main",
                        bgcolor: projectReviewed ? "action.selected" : "action.hover",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ sm: "center" }}
                        spacing={1}
                        sx={{ mb: 1.5 }}
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          اطلاعات پروژه
                          {projectReviewed ? " (بررسی شد)" : ""}
                        </Typography>
                        {!projectReviewed ? (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={beginQuoteAfterReview}
                            disabled={selectedRequestLocked}
                          >
                            اطلاعات را دیدم — ادامه به پیشنهاد قیمت
                          </Button>
                        ) : (
                          <Chip size="small" color="success" label="آماده ثبت پیشنهاد" />
                        )}
                      </Stack>

                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <InfoRow
                            label="کد پیگیری"
                            value={
                              selectedRequest.requestReferenceId ||
                              selectedRequest.requestId
                            }
                          />
                          <InfoRow
                            label="متقاضی"
                            value={selectedRequest.applicantFullName}
                          />
                          <InfoRow
                            label="ظرفیت"
                            value={selectedRequest.plantCapacityTitle}
                          />
                          <InfoRow
                            label="نوع نیروگاه"
                            value={selectedRequest.powerPlantTypeTitle}
                          />
                          <InfoRow
                            label="نوع نصب"
                            value={selectedRequest.installationTypeTitle}
                          />
                          <InfoRow
                            label="کاربری محل"
                            value={selectedRequest.locationUsageTypeTitle}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InfoRow
                            label="استان / شهر"
                            value={[selectedRequest.provinceTitle, selectedRequest.cityTitle]
                              .filter(Boolean)
                              .join(" / ")}
                          />
                          <InfoRow label="آدرس" value={selectedRequest.address} />
                          <InfoRow
                            label="مساحت قابل نصب"
                            value={
                              selectedRequest.installableAreaSquareMeter != null
                                ? `${Number(
                                    selectedRequest.installableAreaSquareMeter
                                  ).toLocaleString("fa-IR")} م²`
                                : null
                            }
                          />
                          <InfoRow
                            label="نیاز به وام"
                            value={selectedRequest.needsLoan ? "بله" : "خیر"}
                          />
                          {selectedRequest.suggestedPackageTitle ? (
                            <InfoRow
                              label="پکیج پیشنهادی"
                              value={selectedRequest.suggestedPackageTitle}
                            />
                          ) : null}
                        </Grid>
                      </Grid>

                      {selectedRequest.siteVisit ? (
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            خلاصه بازدید سایت
                            {selectedRequest.siteVisit.actualVisitTime
                              ? ` — ${toJalaliFullDate(
                                  selectedRequest.siteVisit.actualVisitTime
                                )}`
                              : ""}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                              <InfoRow
                                label="وضعیت بازدید"
                                value={selectedRequest.siteVisit.siteVisitStatusTitle}
                              />
                              <InfoRow
                                label="ظرفیت درخواستی"
                                value={
                                  selectedRequest.siteVisit.requestedCapacityKw != null
                                    ? `${selectedRequest.siteVisit.requestedCapacityKw} کیلووات`
                                    : null
                                }
                              />
                              <InfoRow
                                label="ظرفیت پیشنهادی"
                                value={
                                  selectedRequest.siteVisit.proposedCapacityKw != null
                                    ? `${selectedRequest.siteVisit.proposedCapacityKw} کیلووات`
                                    : null
                                }
                              />
                              <InfoRow
                                label="فاز انشعاب"
                                value={selectedRequest.siteVisit.phaseCountTitle}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <InfoRow
                                label="محل نصب"
                                value={
                                  selectedRequest.siteVisit.installationLocationTitle
                                }
                              />
                              <InfoRow
                                label="مساحت قابل نصب"
                                value={
                                  selectedRequest.siteVisit.installableAreaSquareMeter !=
                                  null
                                    ? `${Number(
                                        selectedRequest.siteVisit
                                          .installableAreaSquareMeter
                                      ).toLocaleString("fa-IR")} م²`
                                    : null
                                }
                              />
                              <InfoRow
                                label="محل کنتور"
                                value={boolFa(selectedRequest.siteVisit.hasMeterLocation)}
                              />
                              <InfoRow
                                label="آفتاب‌گیر کامل"
                                value={boolFa(selectedRequest.siteVisit.isFullSunlight)}
                              />
                              <InfoRow
                                label="بام مشاع"
                                value={boolFa(selectedRequest.siteVisit.isSharedRooftop)}
                              />
                            </Grid>
                            {selectedRequest.siteVisit.description ? (
                              <Grid item xs={12}>
                                <InfoRow
                                  label="توضیحات بازدید"
                                  value={selectedRequest.siteVisit.description}
                                />
                              </Grid>
                            ) : null}
                          </Grid>
                        </Box>
                      ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          برای این پروژه خلاصه بازدید ثبت‌شده‌ای یافت نشد. قبل از پیشنهاد
                          قیمت، وضعیت بازدید را در کارتابل خود بررسی کنید.
                        </Alert>
                      )}

                      {!projectReviewed && selectedRequest.suggestedPackageId ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 1.5 }}
                        >
                          پس از تایید بررسی، می‌توانید پکیج «
                          {selectedRequest.suggestedPackageTitle || "پیشنهادی"}» را
                          انتخاب کنید یا پکیج دیگری بگذارید.
                        </Typography>
                      ) : null}
                    </Paper>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      ابتدا یک پروژه را از لیست بالا انتخاب کنید تا اطلاعات آن نمایش داده
                      شود. انتخاب پکیج و ثبت پیشنهاد بعد از دیدن اطلاعات پروژه فعال می‌شود.
                    </Alert>
                  </Grid>
                )}

                {projectReviewed && form.requestId ? (
                  <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="مدت اجرا (روز)"
                    type="number"
                    value={form.estimatedDurationDays}
                    onChange={(e) =>
                      handleField("estimatedDurationDays", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <JalaliDatePicker
                    label="تاریخ شروع تخمینی"
                    size="small"
                    fullWidth
                    value={form.estimatedStartDate}
                    onChange={(v) => handleField("estimatedStartDate", v)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {form.proposalMode === "package" ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="تاریخ اعتبار قیمت"
                      value={
                        form.validUntil
                          ? toJalaliDate(form.validUntil)
                          : "—"
                      }
                      InputProps={{ readOnly: true }}
                      helperText="از پکیج انتخاب‌شده"
                    />
                  ) : (
                    <JalaliDatePicker
                      label="تاریخ اعتبار قیمت *"
                      size="small"
                      fullWidth
                      value={form.validUntil}
                      onChange={(v) => handleField("validUntil", v)}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    ۲. نوع پیشنهاد
                  </Typography>
                  <RadioGroup
                    value={form.proposalMode}
                    onChange={(e) => handleField("proposalMode", e.target.value)}
                  >
                    {proposalModeOptions.map((opt) => (
                      <Paper
                        key={opt.mode}
                        variant="outlined"
                        sx={{
                          px: 1.5,
                          py: 1,
                          mb: 1,
                          borderColor:
                            form.proposalMode === opt.mode ? "primary.main" : "divider",
                          bgcolor:
                            form.proposalMode === opt.mode ? "action.hover" : "transparent",
                        }}
                      >
                        <FormControlLabel
                          value={opt.mode}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {opt.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {opt.hint}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                        />
                      </Paper>
                    ))}
                  </RadioGroup>
                </Grid>

                {form.proposalMode === "package" ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>۳. پکیج تجهیزات</InputLabel>
                        <Select
                          label="۳. پکیج تجهیزات"
                          value={form.packageId}
                          onChange={(e) => handleField("packageId", e.target.value)}
                        >
                          {packages.map((p) => {
                            const pkgId = p.id ?? p.Id ?? p.packageId;
                            const title =
                              p.title ?? p.Title ?? p.packageTitle ?? `پکیج ${pkgId}`;
                            const finished =
                              p.priceWithEquipment ?? p.PriceWithEquipment;
                            return (
                              <MenuItem key={pkgId} value={String(pkgId)}>
                                {finished
                                  ? `${title} — ${formatMoney(finished)}`
                                  : title}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      {selectedRequest?.suggestedPackageId ? (
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={applySuggestedPackage}
                          disabled={
                            String(form.packageId) ===
                            String(selectedRequest.suggestedPackageId)
                          }
                        >
                          استفاده از پکیج پیشنهادی
                          {selectedRequest.suggestedPackageTitle
                            ? `: ${selectedRequest.suggestedPackageTitle}`
                            : ""}
                        </Button>
                      ) : null}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                        <Typography variant="caption" color="text.secondary">
                          قیمت تمام‌شده پکیج (تجهیزات + دستمزد)
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {previewLoading
                            ? "..."
                            : `${formatMoney(
                                packagePricing?.finished ||
                                  (Number(form.equipmentCost) ||
                                    packageEquipmentTotal) +
                                    (Number(form.executionCost) || 0)
                              )} ریال`}
                        </Typography>
                      </Paper>
                    </Grid>
                    {form.packageId && packagePricing ? (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            جزئیات قیمت بارگذاری‌شده از پکیج
                          </Typography>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              جمع تجهیزات: {formatMoney(packagePricing.totalEquipment)} ریال
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              دستمزد / اجرا: {formatMoney(packagePricing.labor)} ریال
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              هزینه ارسال:{" "}
                              {packagePricing.contractorPaysDelivery
                                ? "بر عهده پیمانکار (۰ برای مشتری)"
                                : `${formatMoney(packagePricing.deliveryFee)} ریال`}
                            </Typography>
                            {packagePricing.duration ? (
                              <Typography variant="body2" color="text.secondary">
                                مدت اجرا: {packagePricing.duration} روز
                              </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary">
                              اعتبار قیمت:{" "}
                              {packagePricing.priceValidUntil
                                ? toJalaliDate(packagePricing.priceValidUntil)
                                : "ثبت نشده"}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              جمع پیشنهادی: {formatMoney(packagePricing.suggested)} ریال
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ) : null}
                    {form.packageId ? (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            ریز تجهیزات پکیج (قابل ویرایش در مدیریت پکیج)
                          </Typography>
                          {previewLoading ? (
                            <Typography variant="body2" color="text.secondary">
                              در حال بارگذاری...
                            </Typography>
                          ) : previewItems.length === 0 ? (
                            <Alert severity="warning">
                              این پکیج اقلام تجهیزات ندارد. ابتدا در بخش مدیریت پکیج،
                              تجهیزات و قیمت‌ها را تکمیل کنید.
                            </Alert>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>نوع</TableCell>
                                  <TableCell>برند / مدل</TableCell>
                                  <TableCell>تعداد</TableCell>
                                  <TableCell>قیمت واحد</TableCell>
                                  <TableCell>جمع</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {previewItems.map((row, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      {row.equipmentType ?? row.EquipmentType}
                                    </TableCell>
                                    <TableCell>
                                      {[row.brand ?? row.Brand, row.model ?? row.Model]
                                        .filter(Boolean)
                                        .join(" / ")}
                                    </TableCell>
                                    <TableCell>{row.quantity ?? row.Quantity}</TableCell>
                                    <TableCell>
                                      {formatMoney(row.unitCost ?? row.UnitCost)}
                                    </TableCell>
                                    <TableCell>
                                      {formatMoney(row.totalCost ?? row.TotalCost)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Paper>
                      </Grid>
                    ) : null}
                  </>
                ) : null}

                {form.proposalMode === "custom" ? (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      چون پکیج انتخاب نشده، قیمت تجهیزات را همین‌جا وارد کنید. برای پروژه‌های
                      تکراری بهتر است پکیج بسازید تا دوباره کاری نشود.
                    </Alert>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      اقلام تجهیزات
                    </Typography>
                    {lineItems.map((row, idx) => (
                      <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="عنوان"
                            value={row.title}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = { ...next[idx], title: e.target.value };
                              setLineItems(next);
                            }}
                          />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="تعداد"
                            type="number"
                            value={row.quantity}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = { ...next[idx], quantity: e.target.value };
                              setLineItems(next);
                            }}
                          />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="واحد"
                            value={row.unit}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = { ...next[idx], unit: e.target.value };
                              setLineItems(next);
                            }}
                          />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="قیمت واحد"
                            type="number"
                            value={row.unitCost}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = { ...next[idx], unitCost: e.target.value };
                              setLineItems(next);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <IconButton
                            color="error"
                            onClick={() =>
                              setLineItems((prev) => prev.filter((_, i) => i !== idx))
                            }
                            disabled={lineItems.length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setLineItems((prev) => [...prev, emptyLineItem()])}
                    >
                      افزودن قلم
                    </Button>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    هزینه‌های پیشنهاد
                    {form.proposalMode === "package"
                      ? " (از پکیج بارگذاری شده — قابل تعدیل)"
                      : ""}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="دستمزد / هزینه اجرا"
                    type="number"
                    value={form.executionCost}
                    onChange={(e) => handleField("executionCost", e.target.value)}
                    helperText={
                      form.proposalMode === "package"
                        ? "پیش‌فرض از دستمزد پکیج؛ در صورت نیاز برای این پروژه تغییر دهید."
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="هزینه حمل"
                    type="number"
                    value={form.logisticsCost}
                    onChange={(e) => handleField("logisticsCost", e.target.value)}
                    helperText={
                      form.proposalMode === "package"
                        ? "پیش‌فرض از هزینه ارسال پکیج"
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="سایر هزینه‌ها"
                    type="number"
                    value={form.otherCost}
                    onChange={(e) => handleField("otherCost", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={0.5}>
                      {form.proposalMode === "package" ? (
                        <Typography variant="body2" color="text.secondary">
                          تجهیزات پکیج:{" "}
                          {formatMoney(form.equipmentCost || packageEquipmentTotal)} ریال
                        </Typography>
                      ) : null}
                      {form.proposalMode === "custom" ? (
                        <Typography variant="body2" color="text.secondary">
                          تجهیزات سفارشی: {formatMoney(customEquipmentTotal)} ریال
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        دستمزد/اجرا + حمل + سایر:{" "}
                        {formatMoney(
                          (Number(form.executionCost) || 0) +
                            (Number(form.logisticsCost) || 0) +
                            (Number(form.otherCost) || 0)
                        )}{" "}
                        ریال
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        جمع کل پیشنهاد: {formatMoney(computedTotal)} ریال
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={saving || selectedRequestLocked}
                      onClick={handleCreateDraft}
                    >
                      {saving
                        ? "..."
                        : form.parentCostQuoteId
                        ? "ثبت نسخه اصلاحی"
                        : "ثبت پیش‌نویس"}
                    </Button>
                  </Stack>
                </Grid>
                  </>
                ) : null}
              </Grid>

              <Divider />

              <Typography variant="subtitle1" fontWeight={700}>
                پیشنهادهای من
              </Typography>
              {loading ? (
                <Typography color="text.secondary">در حال بارگذاری...</Typography>
              ) : quotes.length === 0 ? (
                <Typography color="text.secondary">
                  پیشنهاد هزینه‌ای ثبت نشده است.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {quotes.map((q) => (
                    <Paper key={q.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                        <Chip
                          label={q.statusTitle || "—"}
                          color={statusColor(q.status)}
                          size="small"
                        />
                        <Chip
                          size="small"
                          label={
                            requestLabels[String(q.requestId)] ||
                            `درخواست ${q.requestId}`
                          }
                        />
                        <Chip size="small" label={q.quoteTypeTitle || "—"} />
                        {q.packageTitle ? (
                          <Chip size="small" variant="outlined" label={q.packageTitle} />
                        ) : null}
                        <Chip
                          size="small"
                          label={`جمع: ${formatMoney(q.totalContractorCost)}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`نسخه ${q.revisionNo}`}
                        />
                        {q.validUntil ? (
                          <Chip
                            size="small"
                            color={
                              new Date(q.validUntil) < new Date(new Date().toDateString())
                                ? "error"
                                : "default"
                            }
                            variant="outlined"
                            label={`اعتبار تا: ${toJalaliDate(q.validUntil)}`}
                          />
                        ) : null}
                        {q.submittedAt ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`ارسال: ${toJalaliFullDate(q.submittedAt)}`}
                          />
                        ) : null}
                        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
                          {Number(q.status) === DRAFT_STATUS ? (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SendIcon />}
                                disabled={submittingId === q.id}
                                onClick={() => handleSubmit(q.id)}
                              >
                                ارسال
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                disabled={deletingId === q.id}
                                onClick={() => setDeleteTarget(q)}
                              >
                                حذف
                              </Button>
                            </>
                          ) : null}
                          {Number(q.status) === REJECTED_STATUS ? (
                            <Button size="small" onClick={() => startRevision(q)}>
                              نسخه جدید
                            </Button>
                          ) : null}
                        </Stack>
                      </Stack>
                      {q.rejectReason ? (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {q.rejectReason}
                        </Typography>
                      ) : null}
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <DeleteConfirm
        open={Boolean(deleteTarget)}
        onClose={(shouldRefresh) => {
          setDeleteTarget(null);
          if (shouldRefresh) {
            loadData();
          }
        }}
        onConfirm={handleConfirmDeleteDraft}
      />
    </Grid>
  );
};

export default ContractorCostQuotesPage;
